import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    privacy: false
  });

  const [step, setStep] = useState<'input' | 'confirm'>('input');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // ① 入力画面 → 確認画面へ
    if (step === 'input') {
      if (!formData.privacy) {
        alert('プライバシーポリシーに同意してください。');
        return;
      }
      setSubmitStatus('idle');
      setStep('confirm');
      return;
    }

    // ② 確認画面 → 送信処理（send.php）
    if (step === 'confirm') {
      setIsSubmitting(true);
      setSubmitStatus('idle');

      try {
        const formBody = new URLSearchParams();
        formBody.append('name', formData.name);
        formBody.append('company', formData.company);
        formBody.append('email', formData.email);
        formBody.append('phone', formData.phone);
        formBody.append('service', formData.service);
        formBody.append('message', formData.message);

        const response = await fetch('/send.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formBody.toString()
        });

        if (response.ok) {
          setSubmitStatus('success');
          // 送信後は入力画面に戻して、フォームをリセット
          setFormData({
            name: '',
            company: '',
            email: '',
            phone: '',
            service: '',
            message: '',
            privacy: false
          });
          setStep('input');
        } else {
          setSubmitStatus('error');
        }
      } catch (error) {
        setSubmitStatus('error');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <section id="contact" className="py-32 bg-gradient-to-b from-amber-50 to-red-50 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-96 h-96 bg-red-900 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber-600 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block mb-6 px-8 py-3 bg-red-900 rounded-full border-2 border-amber-600">
            <span className="text-amber-100 text-sm font-black tracking-widest" style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}>
              CONTACT US
            </span>
          </div>
          <h2 className="text-5xl md:text-6xl font-black text-red-900 mb-6" style={{ fontFamily: '"Shippori Mincho", serif' }}>
            お問い合わせ
          </h2>
          <p className="text-xl text-stone-700 max-w-2xl mx-auto leading-relaxed font-bold" style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}>
            ウェブコンテンツに関するご相談は、お気軽にお問い合わせください
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          data-readdy-form
          className="bg-white rounded-3xl p-8 md:p-12 border-4 border-red-900/20 shadow-2xl"
        >
          {/* 入力画面 */}
          {step === 'input' && (
            <>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-red-900 font-black mb-2" style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}>
                    お名前 <span className="text-amber-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-amber-50 border-2 border-red-900/20 rounded-xl text-stone-800 placeholder-stone-400 focus:outline-none focus:border-amber-600 transition-colors text-sm font-bold"
                    style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}
                    placeholder="山田 太郎"
                  />
                </div>

                <div>
                  <label className="block text-red-900 font-black mb-2" style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}>
                    会社名・団体名
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full px-4 py-3 bg-amber-50 border-2 border-red-900/20 rounded-xl text-stone-800 placeholder-stone-400 focus:outline-none focus:border-amber-600 transition-colors text-sm font-bold"
                    style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}
                    placeholder="株式会社〇〇"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-red-900 font-black mb-2" style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}>
                    メールアドレス <span className="text-amber-600">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 bg-amber-50 border-2 border-red-900/20 rounded-xl text-stone-800 placeholder-stone-400 focus:outline-none focus:border-amber-600 transition-colors text-sm font-bold"
                    style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}
                    placeholder="example@company.com"
                  />
                </div>

                <div>
                  <label className="block text-red-900 font-black mb-2" style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}>
                    電話番号
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 bg-amber-50 border-2 border-red-900/20 rounded-xl text-stone-800 placeholder-stone-400 focus:outline-none focus:border-amber-600 transition-colors text-sm font-bold"
                    style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}
                    placeholder="03-1234-5678"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-red-900 font-black mb-2" style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}>
                  ご希望のサービス <span className="text-amber-600">*</span>
                </label>
                <div className="relative">
                  <select
                    name="service"
                    required
                    value={formData.service}
                    onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                    className="w-full px-4 py-3 pr-8 bg-amber-50 border-2 border-red-900/20 rounded-xl text-stone-800 focus:outline-none focus:border-amber-600 transition-colors appearance-none cursor-pointer text-sm font-bold"
                    style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}
                  >
                    <option value="">選択してください</option>
                    <option value="企画">企画</option>
                    <option value="制作">制作</option>
                    <option value="管理・運営">管理・運営</option>
                    <option value="販売">販売</option>
                    <option value="プロデュース">プロデュース</option>
                    <option value="その他">その他</option>
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-stone-600 pointer-events-none w-5 h-5 flex items-center justify-center"></i>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-red-900 font-black mb-2" style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}>
                  お問い合わせ内容 <span className="text-amber-600">*</span>
                </label>
                <textarea
                  name="message"
                  required
                  rows={6}
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-3 bg-amber-50 border-2 border-red-900/20 rounded-xl text-stone-800 placeholder-stone-400 focus:outline-none focus:border-amber-600 transition-colors resize-none text-sm font-bold"
                  style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}
                  placeholder="ご相談内容をご記入ください（500文字以内）"
                ></textarea>
                <div className="text-right text-stone-600 text-sm mt-1 font-bold" style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}>
                  {formData.message.length}/500
                </div>
              </div>

              <div className="mb-8">
                <label className="flex items-start cursor-pointer group">
                  <input
                    type="checkbox"
                    name="privacy"
                    checked={formData.privacy}
                    onChange={(e) => setFormData({ ...formData, privacy: e.target.checked })}
                    className="mt-1 w-5 h-5 rounded border-red-900 text-amber-600 focus:ring-amber-600 focus:ring-offset-amber-50 cursor-pointer"
                    required
                  />
                  <span className="ml-3 text-stone-700 text-sm leading-relaxed font-bold" style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}>
                    <a href="/privacy" className="text-red-900 hover:text-amber-600 underline font-black">プライバシーポリシー</a>に同意します <span className="text-amber-600">*</span>
                  </span>
                </label>
              </div>
            </>
          )}

          {/* 確認画面 */}
          {step === 'confirm' && (
            <div className="space-y-6 mb-8">
              <h3 className="text-2xl font-black text-red-900 mb-4" style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}>
                入力内容のご確認
              </h3>

              <div className="bg-amber-50 border-2 border-red-900/20 rounded-xl p-4 space-y-3 text-sm font-bold" style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}>
                <p><span className="text-red-900">お名前：</span>{formData.name}</p>
                <p><span className="text-red-900">会社名・団体名：</span>{formData.company || '（未入力）'}</p>
                <p><span className="text-red-900">メールアドレス：</span>{formData.email}</p>
                <p><span className="text-red-900">電話番号：</span>{formData.phone || '（未入力）'}</p>
                <p><span className="text-red-900">ご希望のサービス：</span>{formData.service}</p>
                <div>
                  <p className="text-red-900 mb-1">お問い合わせ内容：</p>
                  <p className="whitespace-pre-wrap">{formData.message}</p>
                </div>
              </div>

              <p className="text-stone-700 text-sm" style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}>
                上記の内容でよろしければ「この内容で送信する」を押してください。修正する場合は「戻って修正する」を押してください。
              </p>
            </div>
          )}

          {submitStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-100 border-2 border-green-600 rounded-xl text-green-800 text-center font-bold" style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}>
              お問い合わせを受け付けました。担当者より折り返しご連絡いたします。
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-100 border-2 border-red-600 rounded-xl text-red-800 text-center font-bold" style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}>
              送信に失敗しました。もう一度お試しください。
            </div>
          )}

          <div className={step === 'confirm' ? 'flex gap-4' : ''}>
            {step === 'confirm' && (
              <button
                type="button"
                onClick={() => setStep('input')}
                className="w-1/2 px-8 py-5 bg-white text-red-900 text-base font-black rounded-xl border-2 border-red-900 hover:bg-amber-50 transition-all duration-300"
                style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}
              >
                戻って修正する
              </button>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className={`${
                step === 'confirm' ? 'w-1/2' : 'w-full'
              } px-8 py-5 bg-gradient-to-r from-red-900 to-amber-700 text-amber-100 text-xl font-black rounded-xl hover:from-red-800 hover:to-amber-600 transition-all duration-300 shadow-2xl hover:shadow-red-900/50 disabled:opacity-50 disabled:cursor-not-allowed border-2 border-amber-600 whitespace-nowrap cursor-pointer`}
              style={{ fontFamily: '"Zen Kaku Gothic New", sans-serif' }}
            >
              {step === 'input'
                ? '確認画面へ'
                : isSubmitting
                ? '送信中...'
                : 'この内容で送信する'}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
